import { Component, ElementRef, forwardRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppService } from '@App';
import { AccountSettings, CaseDocumentTypes, DocType, FeatureToggle } from '@Enums';
import { CaseDocumentsDTO } from '@Models/domain/dto';
import { AccountSettingsService, CaseSummaryService, FeatureManagerService, NotificationService, NotificationSeverity } from '@Services';
import { CaseRequirementResponse, EvidenceResponse } from '@Underwriting/models';
import { UWRequirementsDataService, UWWorkbenchService } from '@Underwriting/services';
import { FileUtils, Utils } from '@Utils';
import { SubSink } from 'subsink';

import { AudioPlayer, ImgViewer, JsonViewer, PlainTextViewer, TiffProps, TiffViewer } from '.';
import PdfViewer, { ZoomLevel } from './components/pdf-viewer/pdf-viewer.component';
import { ViewDocumentDialogData } from './models';
import { DomSanitizer } from '@angular/platform-browser';
import { SharedModule } from '@Shared/shared.module';

@Component({
    selector: 'view-document-dialog',
    templateUrl: './view-document-dialog.component.html',
    styleUrls: ['./view-document-dialog.component.scss'],
    imports: [SharedModule, forwardRef(() => PdfViewer),forwardRef(() => JsonViewer), forwardRef(() =>PlainTextViewer), forwardRef(() => ImgViewer), forwardRef(() =>TiffViewer), forwardRef(() =>AudioPlayer)]
})
export default class ViewDocumentDialog {
    @ViewChild('audioControl') audioControlRef: ElementRef;

    public readonly Error_UnexpectedUpload = 'Something unexpected went wrong trying to upload the document.';
    public readonly Error_NoDocuments = 'No documents selected.';
    public readonly Error_MultipleDocuments = 'Only one document can be uploaded.  Multiple documents where selected.';
    public readonly Error_DocumentExtension = 'Only pdf and Tiff files are allowed to be uploaded';
    public readonly Error_UploadFailed = 'Upload failed.';
    public readonly Error_UploadCanceled = 'Upload Canceled.';
    public readonly Msg_AttachmentUploaded = 'Attachment added successfully';

    title = '';
    caseRequirementName = '';
    subs = new SubSink();

    fullscreen = false;
    hideClose = false;
    hideActions = false;
    showMarkReviewed = false;
    disableMarkReviewed = false;
    reviewDocumentEnabled = false;

    renderAsTextTypes = [DocType.TEXT, DocType.HTML, DocType.XML, DocType.XSLT, DocType.XSD];
    isJsonFile = false;
    isTextFile = false;
    isTiffFile = false;
    isImageFile = false;
    isAudioFile = false;
    isPdfFile = false;
    isPdfLoaded = false;
    isRemoteAttachment = false;
    caseDocumentUrl: any;
    isWorkbenchReadOnly = false;
    openRemoteInNewTab = false;
    src = '';
    zoomLevel = ZoomLevel.Auto;

    evidence: EvidenceResponse;
    hasBeenReviewed = false;

    tiffProps: TiffProps;

    caseRequirement: CaseRequirementResponse;
    remoteMd5 = 'application/remoteFile';

    autoReviewDocumentSetting = false;
    caseDocumentPath: string;
    openAttachmentInIframe = false;

    constructor(
        public dialogRef: MatDialogRef<ViewDocumentDialog>,
        @Inject(MAT_DIALOG_DATA) public data: ViewDocumentDialogData,
        private caseSummaryService: CaseSummaryService,
        protected notificationService: NotificationService,
        protected appService: AppService,
        public uwWorkbenchService: UWWorkbenchService,
        private featureManagerService: FeatureManagerService,
        private uwRequirementsDataService: UWRequirementsDataService,        
        private sanitizer: DomSanitizer,
        private readonly accountSettingsService: AccountSettingsService
    ) {
    }

    //#region Helpers

    getFileType(caseDocument: CaseDocumentsDTO) {
        if (FileUtils.validFileType(caseDocument.generateTo)) return caseDocument.generateTo as DocType.Image;
        if (FileUtils.validFileType(caseDocument.doctypeId)) return caseDocument.doctypeId as DocType;

        return FileUtils.getFileTypeFromExtension(caseDocument.remoteName);
    }

    setFileType() {
        const fileType = this.getFileType(this.data.caseDocument);

        this.isJsonFile = fileType == DocType.JSON;
        this.isTiffFile = fileType == DocType.TIFF;
        this.isImageFile = fileType == DocType.Image;
        this.isAudioFile = fileType == DocType.Audio;
        this.isPdfFile = fileType == DocType.PDF;
        this.isTextFile = this.renderAsTextTypes.includes(fileType);
    }

    getDocumentUri() {
        const { client, account, caseId, caseDocument, caseDocumentType } = this.data;


        if (caseDocument.documentMd5 === this.remoteMd5) {
            this.caseDocumentPath = caseDocument.path;
            this.caseDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(caseDocument.path);
            this.isRemoteAttachment = true;
            
            this.getAutoReviewDocumentSetting();
            return;
        }
        const loadDocument$ = caseDocumentType == CaseDocumentTypes.Attachment
            ? this.caseSummaryService.downloadAttachedDocument(client.id, account.id, caseId, caseDocument.id, true)
            : this.caseSummaryService.getCaseDocumentUri(client.id, account.id, caseId, caseDocument.id);

        loadDocument$.subscribe({
            next: async (objectUrl: string) => {
                this.src = objectUrl; 
                this.getAutoReviewDocumentSetting();
            } ,
            error: _error => {
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: 'Error trying to download the documents.' });
            }
        });
    }

    formatCaseRequirementName() {
        if (!this.caseRequirement)
            return '';

        let requirementName = (this.caseRequirement.subtitle?.trim())
            ? `${this.caseRequirement.name}: ${this.caseRequirement.subtitle}`
            : this.caseRequirement.name;

        requirementName = requirementName.trim();
        return requirementName;
    }

    setDialogTitle() {
        this.title = `${this.data.caseDocument?.docTemplateName ?? this.data.caseDocument?.remoteName}`;
    }

    //#endregion
    //#region Subscriptions

    subsribeToFeatureToggles() {
        const { client } = this.data;

        this.subs.add(this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchFeatureReviewDocumentsClients, client.id).subscribe({
            next: isEnabled => {
                this.reviewDocumentEnabled = isEnabled;
                this.showMarkReviewed = this.caseRequirement && this.reviewDocumentEnabled;
            }
        }));
    }

    subscribeToCaseRequirements() {
        if (this.data.caseRequirement) {
            this.caseRequirement = this.data.caseRequirement;
            this.caseRequirementName = this.formatCaseRequirementName();
            this.data.caseRequirementId = this.caseRequirement.id;
            return;
        }

        if (!this.data.caseRequirementId) return;

        this.subs.add(this.uwRequirementsDataService.loadCaseRequirements(this.data.client.code, this.data.caseId).subscribe(caseRequirementResponses => {
            this.caseRequirement = caseRequirementResponses.find(x => x.id == this.data.caseRequirementId);
            this.evidence = this.caseRequirement?.evidence.find(x => x.caseDocumentId == this.data.caseDocument.id);
            this.hasBeenReviewed = this.evidence?.reviews && this.evidence.reviews.length > 0;
            this.disableMarkReviewed = this.hasBeenReviewed;
            this.showMarkReviewed = this.caseRequirement && this.reviewDocumentEnabled;
        }));
    }

    getAutoReviewDocumentSetting() {
        this.subs.add(this.accountSettingsService.getAccountSetting(this.data.account, AccountSettings.autoReviewDocumentSetting).subscribe(accountSetting => {
            if (!accountSetting) {
                 this.openRemoteDocument();
                return false;
            }

            // Empty Guids should set the value to empty string
            if (accountSetting.value === Utils.emptyGuid) this.autoReviewDocumentSetting = false;
            else this.autoReviewDocumentSetting = accountSetting.value === 'true';

            if (this.autoReviewDocumentSetting) {
                this.handleMarkReviewed();
            }else{                
                this.openRemoteDocument();                
            }
        }));
    }

    openRemoteDocument(){
        if (!this.isRemoteAttachment) return;

        if (this.openRemoteInNewTab) {
            if (this.autoReviewDocumentSetting) {                
                window.open(this.caseDocumentPath, '_blank').focus();                
            }else{
                this.openAttachmentInIframe = true;
            }
        }else{
            if (this.autoReviewDocumentSetting) {                    
                window.location.href = this.caseDocumentPath;
            }else{            
                this.openAttachmentInIframe = true;
            }
        }
    }
    //#endregion
    //#region Lifecycle

    ngOnInit() {
        this.setFileType();
        this.uwWorkbenchService.setClientInfo(this.data.client);
        if (this.data.caseRequirementId)
            this.subs.add(this.uwWorkbenchService.viewedEvidence(this.data.account.id, this.data.caseRequirementId, this.data.caseDocument).subscribe());

        this.zoomLevel = this.data.zoom || ZoomLevel.Auto;
        this.setDialogTitle();
        this.caseRequirementName = this.formatCaseRequirementName();
        this.fullscreen = this.data.fullscreen;
        this.hideClose = this.data.hideClose;
        this.hideActions = this.hideClose; // we only have "close", if we hide it, hide the whole bar.
        this.openRemoteInNewTab = this.data.openRemoteInNewTab || false;
        this.subsribeToFeatureToggles();
        this.subscribeToCaseRequirements();
        this.getDocumentUri();

        if (!this.appService.checkACL('Case Summary Workbench', 'W')){
            this.isWorkbenchReadOnly = true;
        }
    }


    ngOnDestory() {
        this.subs.unsubscribe();
    }

    //#endregion
    //#region Handlers

    handleCancel() {
        this.dialogRef.close({
            action: "cancel"
        });
    }

    handleMarkReviewed() {
        if (!this.data.caseRequirementId || this.data.caseRequirementId === null) return;

        this.disableMarkReviewed = true;
        this.subs.add(this.uwWorkbenchService.reviewedEvidence(this.data.account.id, this.data.caseRequirementId, this.data.caseDocument).subscribe({
            next: () => { 
                if (this.autoReviewDocumentSetting) {
                    this.openRemoteDocument();
                }
            },
            error: _err => {
                this.disableMarkReviewed = false;
            }
        }));
    }

    //#endregion
}
